import { ReactNode } from 'react';

export type ButtonGroupProps = {
  /** Button components to group together */
  children: ReactNode;
  /** Additional CSS classes applied to the container */
  className?: string;
  /** Grouping style - default merges buttons with shared borders, separated adds gap between buttons */
  variant?: 'default' | 'separated';
  /** Layout direction - horizontal arranges buttons in a row, vertical stacks them */
  orientation?: 'horizontal' | 'vertical';
};

/**
 * ButtonGroup - Groups related buttons together
 */
function ButtonGroup({
  children,
  className = '',
  variant = 'default',
  orientation = 'horizontal',
}: Readonly<ButtonGroupProps>) {
  const baseClasses = 'inline-flex';
  const orientationClasses =
    orientation === 'horizontal' ? 'flex-row' : 'flex-col';

  let variantClasses = '';
  if (variant === 'separated') {
    variantClasses = 'gap-2';
  } else if (orientation === 'horizontal') {
    variantClasses =
      '[&>*]:rounded-none [&>*:first-child]:rounded-l-md [&>*:last-child]:rounded-r-md [&>*:not(:last-child)]:border-r-0';
  } else {
    variantClasses =
      '[&>*]:rounded-none [&>*:first-child]:rounded-t-md [&>*:last-child]:rounded-b-md [&>*:not(:last-child)]:border-b-0';
  }

  return (
    <div
      data-slot="button-group"
      className={`${baseClasses} ${orientationClasses} ${variantClasses} ${className}`}
    >
      {children}
    </div>
  );
}

ButtonGroup.displayName = 'ButtonGroup';

export default ButtonGroup;
